import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Stack, SimpleGrid, Card, Group, Title, Text } from '@repletoryreach/sdk-ui';
import { Alert, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { Button } from '@repletoryreach/sdk-ui';
import { useAuth } from '@repletoryreach/sdk-auth';
import { admin } from '@repletoryreach/sdk-api';
import { useIntegrationProviders, useIntegrations } from '../../data/useIntegrations';
import { useFeatureFlags } from '../../data/useFeatureFlags';
import { useT } from '../../i18n/I18nProvider';
import { HubCard } from '../../components/HubCard';
import { StatCard } from '../../components/StatCard';
import { KeyIcon, PersonIcon, SlidersIcon } from '../../components/hubIcons';
import { formatDateTime, formatRelative } from '../../lib/format';
import './hub.css';

const icon = (children: React.ReactNode) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false">
    {children}
  </svg>
);

const PlugIcon = icon(<><path d="M9 3v5M15 3v5" /><path d="M6 8h12v3.5a6 6 0 0 1-12 0z" /><path d="M12 17.5V21" /></>);
const FlagIcon = icon(<><path d="M6 21V4" /><path d="M6 4.5h11l-2 3.5 2 3.5H6" /></>);
const KeyStatIcon = icon(<><circle cx="8" cy="12" r="3.5" /><path d="M11.5 12H20M17 12v3M20 12v2.5" /></>);
const ShieldIcon = icon(<><path d="M12 3l7 3v5.5c0 4.4-3 7.9-7 9.5-4-1.6-7-5.1-7-9.5V6z" /><path d="M9 12l2.2 2.2L15.5 10" /></>);

/** The count of configured rows in the design's shorthand: platform scope, no target. */
const isPlatformScope = (r: admin.Integration) => r.scope_id === 1 && !r.scope_target;

export default function SettingsPage() {
  const t = useT();
  const { user } = useAuth();

  /*
   * Every figure on this page comes from the same cached query its destination
   * page uses, so the hub costs no extra round trip once an operator has been
   * anywhere in this section. A query still in flight renders a skeleton bar
   * rather than a zero that reads as "nothing is configured".
   */
  const providersQ = useIntegrationProviders();
  const rowsQ = useIntegrations();
  const flagsQ = useFeatureFlags();
  const keysQ = useQuery({
    queryKey: ['admin', 'api-keys', { includeRevoked: true }],
    queryFn: () => admin.listApiKeys({ include_revoked: true })
  });

  const providers = providersQ.data?.data ?? [];
  const rows = useMemo(() => (rowsQ.data?.data ?? []).filter(isPlatformScope), [rowsQ.data]);
  const flags = flagsQ.data?.data ?? [];
  const keys = keysQ.data?.data ?? [];

  const configuredCount = rows.length;
  const connectedCount = rows.filter((r) => r.enabled && r.last_test_status === 'ok').length;
  const flagsOn = flags.filter((f) => f.default_enabled).length;
  const activeKeys = keys.filter((k) => k.is_active).length;
  const revokedKeys = keys.length - activeKeys;

  const integrationsLoading = providersQ.isLoading || rowsQ.isLoading;
  const integrationsFailed = Boolean(providersQ.error || rowsQ.error);
  const twoFactorOn = user?.two_factor_enabled ?? false;

  /*
   * The hub's two live counts sit on the destination rows as well as on the
   * cards above, because the row is what an operator clicks and a count beside
   * the name is what tells them whether clicking is worth it.
   */
  const integrationsMeta = integrationsLoading || integrationsFailed
    ? undefined
    : t('settings.hub.meta.integrations', { configured: configuredCount, total: providers.length });
  const flagsMeta = flagsQ.isLoading || flagsQ.error
    ? undefined
    : t('settings.hub.meta.flags', { count: flags.length });

  /* The five most recently changed providers: the panel answers "what did
     somebody touch", so it sorts by when the row was written, not by name. */
  const recent = useMemo(
    () => [...rows]
      .sort((a, b) => new Date(b.updated_at ?? 0).getTime() - new Date(a.updated_at ?? 0).getTime())
      .slice(0, 5),
    [rows]
  );
  const providerName = (code: string) => providers.find((p) => p.code === code)?.display_name ?? code;

  return (
    <Stack gap="lg">
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <StatCard
          icon={PlugIcon}
          tone="primary"
          label={t('settings.hub.stats.integrations.label')}
          value={configuredCount}
          hint={t('settings.hub.stats.integrations.hint', { count: connectedCount })}
          loading={integrationsLoading}
          to="/settings/integrations"
          linkLabel={t('settings.hub.stats.integrations.link')}
        />
        <StatCard
          icon={FlagIcon}
          tone="info"
          label={t('settings.hub.stats.flags.label')}
          value={flags.length}
          hint={t('settings.hub.stats.flags.hint', { count: flagsOn })}
          loading={flagsQ.isLoading}
          to="/settings/feature-flags"
          linkLabel={t('settings.hub.stats.flags.link')}
        />
        <StatCard
          icon={KeyStatIcon}
          tone="neutral"
          label={t('settings.hub.stats.apiKeys.label')}
          value={activeKeys}
          hint={t('settings.hub.stats.apiKeys.hint', { count: revokedKeys })}
          loading={keysQ.isLoading}
          to="/settings/api-keys"
          linkLabel={t('settings.hub.stats.apiKeys.link')}
        />
        {/* The one card that is about the operator rather than the platform.
            It needs no query: the session already knows. */}
        <StatCard
          icon={ShieldIcon}
          tone={twoFactorOn ? 'success' : 'warning'}
          label={t('settings.hub.stats.twoFactor.label')}
          value={twoFactorOn ? t('settings.hub.stats.twoFactor.on') : t('settings.hub.stats.twoFactor.off')}
          hint={twoFactorOn ? t('settings.hub.stats.twoFactor.hintOn') : t('settings.hub.stats.twoFactor.hintOff')}
          to="/settings/security"
          linkLabel={t('settings.hub.stats.twoFactor.link')}
        />
      </SimpleGrid>

      <Stack gap="sm">
        <Text role="overline" c="var(--rr-color-text-muted)">{t('settings.hub.section.title')}</Text>
        <SimpleGrid cols={{ base: 1, md: 2, xl: 3 }} spacing="md">
          <HubCard
            icon={PersonIcon}
            title={t('settings.hub.you.title')}
            description={t('settings.hub.you.description')}
            rows={[
              { label: t('settings.layout.nav.profile'), to: '/settings/profile' },
              { label: t('settings.layout.nav.appearance'), to: '/settings/appearance' },
              { label: t('settings.layout.nav.security'), to: '/settings/security' }
            ]}
          />
          <HubCard
            icon={SlidersIcon}
            title={t('settings.hub.platform.title')}
            description={t('settings.hub.platform.description')}
            rows={[
              { label: t('settings.layout.nav.integrations'), to: '/settings/integrations', meta: integrationsMeta },
              { label: t('settings.layout.nav.featureFlags'), to: '/settings/feature-flags', meta: flagsMeta }
            ]}
          />
          <HubCard
            icon={KeyIcon}
            title={t('settings.hub.access.title')}
            description={t('settings.hub.access.description')}
            rows={[
              { label: t('settings.layout.nav.apiKeys'), to: '/settings/api-keys' },
              { label: t('settings.layout.nav.backup'), to: '/settings/backup' }
            ]}
          />
        </SimpleGrid>
      </Stack>

      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between" align="center" wrap="wrap">
            <Title order={5}>{t('settings.hub.integrations.title')}</Title>
            <Link to="/settings/integrations" style={{ color: 'var(--rr-color-primary)', fontSize: 12.5, fontWeight: 600, textDecoration: 'none' }}>
              {t('settings.hub.integrations.viewAll')} <span aria-hidden>→</span>
            </Link>
          </Group>

          {integrationsFailed && (
            <Alert color="error">
              <Group justify="space-between" align="center" wrap="wrap">
                <Text role="bodySmall">{t('settings.hub.integrations.error')}</Text>
                <Button size="xs" variant="outline" onClick={() => { void providersQ.refetch(); void rowsQ.refetch(); }}>
                  {t('action.retry')}
                </Button>
              </Group>
            </Alert>
          )}

          {!integrationsFailed && !integrationsLoading && recent.length === 0 && (
            <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.hub.integrations.empty')}</Text>
          )}

          {!integrationsFailed && recent.length > 0 && (
            <ul className="rr-settings-hub__list">
              {recent.map((r) => (
                <li key={r.provider_code}>
                  <div className="rr-settings-hub__row">
                    <span className="rr-settings-hub__name">{providerName(r.provider_code)}</span>
                    <RowStatus row={r} label={{
                      disabled: t('status.disabled'),
                      connected: t('settings.integrations.status.connected'),
                      needsAttention: t('settings.integrations.status.needsAttention'),
                      untested: t('settings.integrations.status.enabledUntested')
                    }} />
                    {/* A provider that has never been tested has no timestamp
                        to show, and inventing "just now" from `updated_at`
                        would claim a probe that never ran. */}
                    <span className="rr-settings-hub__when" title={r.last_tested_at ? formatDateTime(r.last_tested_at) : undefined}>
                      {r.last_tested_at
                        ? t('settings.hub.integrations.tested', { when: formatRelative(r.last_tested_at) })
                        : t('settings.hub.integrations.neverTested')}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}

function RowStatus({ row, label }: {
  row: admin.Integration;
  label: { disabled: string; connected: string; needsAttention: string; untested: string };
}) {
  if (!row.enabled) return <StatusBadge status="inactive">{label.disabled}</StatusBadge>;
  if (row.last_test_status === 'ok') return <StatusBadge status="healthy">{label.connected}</StatusBadge>;
  if (row.last_test_status === 'error') return <StatusBadge status="warning">{label.needsAttention}</StatusBadge>;
  return <StatusBadge status="pending">{label.untested}</StatusBadge>;
}
